import React, { useState, useEffect } from 'react';
import axios from 'axios';

const StatusManager = () => {
  const [statusChecks, setStatusChecks] = useState([]);
  const [newClientName, setNewClientName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // API base URL - will work for both local development and Firebase deployment
  const API_BASE = process.env.NODE_ENV === 'production' 
    ? '' // Firebase hosting will proxy /api to functions
    : 'http://localhost:8000'; // Local Express server

  // Fetch all status checks
  const fetchStatusChecks = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/api/status`);
      setStatusChecks(response.data);
      setError('');
    } catch (err) {
      setError('Failed to fetch status checks');
      console.error('Error fetching status checks:', err);
    } finally {
      setLoading(false);
    }
  };

  // Create new status check
  const createStatusCheck = async (e) => {
    e.preventDefault();
    if (!newClientName.trim()) return;

    try {
      setLoading(true);
      const response = await axios.post(`${API_BASE}/api/status`, {
        client_name: newClientName
      });
      setStatusChecks([response.data, ...statusChecks]);
      setNewClientName('');
      setError('');
    } catch (err) {
      setError('Failed to create status check');
      console.error('Error creating status check:', err);
    } finally {
      setLoading(false);
    }
  };

  // Delete status check
  const deleteStatusCheck = async (id) => {
    try {
      setLoading(true);
      await axios.delete(`${API_BASE}/api/status/${id}`);
      setStatusChecks(statusChecks.filter(check => check.id !== id));
      setError('');
    } catch (err) {
      setError('Failed to delete status check');
      console.error('Error deleting status check:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load status checks on component mount
  useEffect(() => {
    fetchStatusChecks();
  }, []);

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">Status Checks Manager</h2>
      
      {/* Error Display */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {/* Create New Status Check Form */}
      <form onSubmit={createStatusCheck} className="mb-6">
        <div className="flex gap-2">
          <input
            type="text"
            value={newClientName}
            onChange={(e) => setNewClientName(e.target.value)}
            placeholder="Enter client name"
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={loading}
          />
          <button
            type="submit"
            disabled={loading || !newClientName.trim()}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Adding...' : 'Add Status Check'}
          </button>
        </div>
      </form>

      {/* Status Checks List */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-700">Recent Status Checks</h3>
          <button
            onClick={fetchStatusChecks}
            disabled={loading}
            className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        {statusChecks.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No status checks found. Create your first one above!
          </div>
        ) : (
          <div className="grid gap-3">
            {statusChecks.map((check) => (
              <div
                key={check.id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                <div>
                  <h4 className="font-medium text-gray-800">{check.client_name}</h4>
                  <p className="text-sm text-gray-500">
                    Created: {new Date(check.timestamp).toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-400">ID: {check.id}</p>
                </div>
                <button
                  onClick={() => deleteStatusCheck(check.id)}
                  disabled={loading}
                  className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* API Info */}
      <div className="mt-8 p-4 bg-gray-100 rounded-lg">
        <h4 className="font-medium text-gray-800 mb-2">API Information</h4>
        <p className="text-sm text-gray-600">
          Environment: <span className="font-mono">{process.env.NODE_ENV || 'development'}</span>
        </p>
        <p className="text-sm text-gray-600">
          API Base URL: <span className="font-mono">{API_BASE || 'Firebase Functions'}</span>
        </p>
        <p className="text-xs text-gray-500 mt-2">
          This component works with both local Express server and Firebase Functions
        </p>
      </div>
    </div>
  );
};

export default StatusManager; 